import { useState } from 'react';

import { ChevronDown, ChevronRight, Clock, Eye, MoreHorizontal, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useOutcomeDisplay } from '@/components/calls/OutcomeBadge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCalls, useUpdateHumanReviewed } from '@/hooks/useCalls';
import { formatDateTime } from '@/utils/format';

import {
  CALL_STATUS_BADGES,
  CALL_DIRECTION_BADGES,
  CALL_TRANSPORT_BADGES,
} from '@repo/shared-types';

import type { CallWithContact } from '@repo/shared-types';

interface CallRowWithHistoryProps {
  call: CallWithContact;
  resolveContactName: (call: CallWithContact) => string;
  // Omitted for read-only viewers (clients view) — hides the Delete action
  onDelete?: (callId: string) => void;
  // Read-only viewers (clients view) can't toggle human-reviewed
  readOnly?: boolean;
}

export const CallRowWithHistory = ({
  call,
  resolveContactName,
  onDelete,
  readOnly = false,
}: CallRowWithHistoryProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const outcomeDisplay = useOutcomeDisplay();
  const updateHumanReviewedMutation = useUpdateHumanReviewed();

  const phoneNumber = call.phone_number || call.contact?.phone || undefined;

  const shouldFetch = isOpen && call.direction === 'INBOUND' && phoneNumber;

  const { data: previousCallsData } = useCalls(
    { search: phoneNumber, limit: 50 },
    { enabled: !!shouldFetch },
  );

  const previousCalls =
    previousCallsData?.data?.filter(
      (prevCall: CallWithContact) => prevCall.id !== call.id && prevCall.campaign,
    ) || [];

  const hasHistory = call.direction === 'INBOUND' && phoneNumber;

  const handleRowClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[role="menu"]')
    ) {
      return;
    }
    navigate(`/calls/${call.id}`);
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <>
      <tr
        onClick={handleRowClick}
        className="group hover:bg-muted/50 cursor-pointer border-b transition-colors"
      >
        {/* Expand toggle */}
        <td className="w-10 px-3 py-3">
          {hasHistory && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          )}
        </td>

        {/* Contact + Phone */}
        <td className="px-3 py-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{resolveContactName(call)}</span>
            <span className="text-muted-foreground font-mono text-xs">{phoneNumber || '-'}</span>
          </div>
        </td>

        {/* Direction + Transport */}
        <td className="px-3 py-3">
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${CALL_DIRECTION_BADGES[call.direction]}`}
            >
              {call.direction}
            </span>
            {call.call_transport && (
              <span
                className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${CALL_TRANSPORT_BADGES[call.call_transport]}`}
              >
                {call.call_transport}
              </span>
            )}
          </div>
        </td>

        {/* Status + Outcome */}
        <td className="px-3 py-3">
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${CALL_STATUS_BADGES[call.status]}`}
            >
              {call.status}
            </span>
            {call.outcome && (
              <span
                className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${outcomeDisplay.badgeClass(call.outcome)}`}
              >
                {outcomeDisplay.label(call.outcome)}
              </span>
            )}
          </div>
        </td>

        {/* Campaign */}
        <td className="px-3 py-3 text-sm">{call.campaign?.name || '-'}</td>

        {/* Configuration */}
        <td className="px-3 py-3 text-sm">{call.configuration?.name || '-'}</td>

        {/* Duration */}
        <td className="px-3 py-3 text-sm tabular-nums">{formatDuration(call.duration)}</td>

        {/* Executed At */}
        <td className="px-3 py-3 text-sm tabular-nums">{formatDateTime(call.executed_at)}</td>

        {/* Human Reviewed */}
        <td className="px-3 py-3">
          <input
            type="checkbox"
            checked={call.human_reviewed || false}
            onChange={(e) => {
              e.stopPropagation();
              updateHumanReviewedMutation.mutate({
                id: call.id,
                humanReviewed: e.target.checked,
              });
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={readOnly || updateHumanReviewedMutation.isPending}
            className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </td>

        {/* Actions */}
        <td className="px-3 py-3 text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/calls/${call.id}`}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </Link>
              </DropdownMenuItem>
              {onDelete && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(call.id)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>

      {/* Collapsible row for previous calls history */}
      {hasHistory && isOpen && (
        <tr>
          <td colSpan={10} className="bg-muted/30 border-b px-3 py-2">
            <Collapsible open={isOpen}>
              <CollapsibleContent>
                <div className="py-2 pl-8">
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <Clock className="h-4 w-4" />
                    Previous Campaign Calls for {phoneNumber}
                  </h4>
                  {previousCalls.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No previous campaign calls found for this number.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {previousCalls.map((prevCall: CallWithContact) => (
                        <div
                          key={prevCall.id}
                          className="bg-background flex items-center justify-between rounded-md border p-3 text-sm"
                        >
                          <div className="flex items-center gap-4">
                            <div>
                              <span className="text-muted-foreground">Campaign:</span>{' '}
                              <Link
                                to={`/campaigns/${prevCall.campaign?.id}`}
                                className="text-primary font-medium hover:underline"
                              >
                                {prevCall.campaign?.name || 'Unknown'}
                              </Link>
                            </div>
                            <div className="flex gap-1">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${CALL_DIRECTION_BADGES[prevCall.direction]}`}
                              >
                                {prevCall.direction}
                              </span>
                              {prevCall.call_transport && (
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${CALL_TRANSPORT_BADGES[prevCall.call_transport]}`}
                                >
                                  {prevCall.call_transport}
                                </span>
                              )}
                            </div>
                            {prevCall.outcome && (
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${outcomeDisplay.badgeClass(prevCall.outcome)}`}
                              >
                                {outcomeDisplay.label(prevCall.outcome)}
                              </span>
                            )}
                            <span className="text-muted-foreground font-mono text-xs">
                              {formatDateTime(prevCall.executed_at)}
                            </span>
                          </div>
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/calls/${prevCall.id}`}>
                              <Eye className="mr-1 h-3 w-3" />
                              View
                            </Link>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </td>
        </tr>
      )}
    </>
  );
};
